import {isEqual} from 'lodash';
import {useEffect, useMemo, useRef} from 'react';

import {safeLocalStorage} from '../../../../../util/localStorage';
import * as QueryTS from '../../../../../util/queryts';
import {Sort} from '../../../../../util/queryTypes';
import {SemanticLegendSettings, YAxisValues} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {SemanticLegendPalette} from './colorPalettes';
import {getColorFromSemanticLegend} from './getColorFromSemanticLegend';
import {
  ConfigValueForRun,
  useConfigValuesAggregation,
} from './useConfigValuesAggregation';
import {useConfigValuesCategories} from './useConfigValuesCategories';
import {
  MetricStatisticsAggregation,
  useMetricStatisticsAggregation,
} from './useMetricStatisticsAggregation';
import {
  MetricStatisticForRun,
  useMetricStatisticsForRun,
} from './useMetricStatisticsForRun';
import {useSemanticLegendSettings} from './useSemanticLegendSettings';

export function getRuns(
  yAxisValue: YAxisValues,
  configValuesForRunCategories: ConfigValueForRun[],
  configValuesForRun: ConfigValueForRun[],
  metricStatisticsForRun: MetricStatisticForRun[]
): string[] {
  switch (yAxisValue) {
    case YAxisValues.Config_Categories:
      return configValuesForRunCategories.map(run => run.runName);
    case YAxisValues.Config_Number:
      return configValuesForRun.map(run => run.runName);
    default:
      return metricStatisticsForRun.map(run => run.run_name);
  }
}

// Performance-optimized function for calculating run colors
function calculateRunColorsOptimized(
  runs: string[],
  semanticLegendSettings: SemanticLegendSettings,
  semanticLegendPalette: SemanticLegendPalette
): Record<string, string> {
  // Early return for empty runs array
  if (runs.length === 0) {
    return {};
  }

  // Pre-allocate object with expected size for better memory management
  const runColors: Record<string, string> = {};

  // Use for loop instead of reduce for better performance (avoid function call overhead)
  for (let i = 0; i < runs.length; i++) {
    const runName = runs[i];
    runColors[runName] = getColorFromSemanticLegend(
      runName,
      semanticLegendSettings,
      semanticLegendPalette,
      1
    );
  }

  return runColors;
}

export const useSemanticLegendSettingsQueryProps = ({
  createSemanticLegendSettings,
  entityName,
  filters,
  order,
  projectName,
  updateSemanticLegendSettings,
  viewID,
  viewName,
  configKeys,
  semanticLegendPalette,
}: {
  createSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;
  updateSemanticLegendSettings: (
    settings: Partial<SemanticLegendSettings>
  ) => void;
  viewID: string;
  viewName: string;
  configKeys?: string[];
  semanticLegendPalette: SemanticLegendPalette;
}) => {
  const {
    constructedSemanticLegendData,
    currentKey,
    semanticLegendSettingsError,
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
    shouldSkipConfigValuesAggregation,
    shouldSkipConfigValuesCategories,
  } = useSemanticLegendSettings(viewID, configKeys);

  const orderString = useMemo(() => QueryTS.sortToOrderString(order), [order]);

  const {
    metricStatisticsAggregation,
    metricStatisticsError,
    metricStatisticsLoading,
  } = useMetricStatisticsAggregation({
    currentKey,
    entityName,
    projectName,
    shouldSkipMetricStatistics,
  });

  const {
    metricStatisticsForRun,
    metricStatisticsForRunError,
    metricStatisticsForRunLoading,
  } = useMetricStatisticsForRun({
    currentKey,
    entityName,
    filters,
    order: orderString,
    projectName,
    shouldSkip: shouldSkipMetricStatistics,
  });

  const {
    configValuesForRun,
    configValuesNumberAggregation,
    configValuesError,
    configValuesLoading,
  } = useConfigValuesAggregation({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesAggregation,
  });

  const {
    configValuesForRun: configValuesForRunCategories,
    configValuesCategories,
    configValuesError: configValuesErrorCategories,
    configValuesLoading: configValuesLoadingCategories,
  } = useConfigValuesCategories({
    currentKey,
    entityName,
    filters,
    order,
    projectName,
    shouldSkip: shouldSkipConfigValuesCategories,
  });

  // Memoize runs calculation since it's used in multiple places
  const currentRuns = useMemo(() => {
    return getRuns(
      constructedSemanticLegendData.yAxisValue,
      configValuesForRunCategories,
      configValuesForRun,
      metricStatisticsForRun
    );
  }, [
    constructedSemanticLegendData.yAxisValue,
    configValuesForRunCategories,
    configValuesForRun,
    metricStatisticsForRun,
  ]);

  // Combine all loading states for consistent checking
  const combinedLoading = useMemo(() => {
    // Always include semantic legend settings loading
    let loading = semanticLegendSettingsLoading;

    // Only include metric loading states if metric queries should NOT be skipped
    if (!shouldSkipMetricStatistics) {
      loading =
        loading || metricStatisticsLoading || metricStatisticsForRunLoading;
    }

    // Only include config loading states if config queries should NOT be skipped
    if (!shouldSkipConfigValuesAggregation) {
      loading = loading || configValuesLoading;
    }

    if (!shouldSkipConfigValuesCategories) {
      loading = loading || configValuesLoadingCategories;
    }

    return loading;
  }, [
    semanticLegendSettingsLoading,
    shouldSkipMetricStatistics,
    metricStatisticsLoading,
    metricStatisticsForRunLoading,
    shouldSkipConfigValuesAggregation,
    configValuesLoading,
    shouldSkipConfigValuesCategories,
    configValuesLoadingCategories,
  ]);

  // Memoize current semantic legend settings to avoid unnecessary recalculations
  const currentSemanticLegendSettings = useMemo(() => {
    if (!constructedSemanticLegendData || combinedLoading) {
      return null;
    }

    return {
      ...constructedSemanticLegendData,
      metricStatisticsForRun,
      configValuesCategories,
      configValuesForRun:
        configValuesForRunCategories.length > 0
          ? configValuesForRunCategories
          : configValuesForRun,
      metricStatistics: {
        ...metricStatisticsAggregation,
        [YAxisValues.Config_Number]: configValuesNumberAggregation,
        [YAxisValues.Config_Categories]: {
          min: 0,
          max: 0,
        },
      },
    };
  }, [
    constructedSemanticLegendData,
    combinedLoading,
    metricStatisticsAggregation,
    configValuesNumberAggregation,
    configValuesForRunCategories,
    configValuesForRun,
    metricStatisticsForRun,
    configValuesCategories,
  ]);

  // Memoize run colors calculation - this is the most expensive operation
  const currentRunColors = useMemo(() => {
    // Don't calculate colors if settings are not ready or any data is still loading
    if (!currentSemanticLegendSettings || combinedLoading) {
      return {};
    }

    return calculateRunColorsOptimized(
      currentRuns,
      currentSemanticLegendSettings,
      semanticLegendPalette
    );
  }, [
    currentRuns,
    currentSemanticLegendSettings,
    semanticLegendPalette,
    combinedLoading,
  ]);

  // Read any saved semantic legend from local storage once per view
  const savedSemanticLegendFromStorage = useMemo(() => {
    const stored = safeLocalStorage.getItem(
      `semanticLegendSettings-${viewName}`
    );
    if (!stored) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(stored);
      return {
        ...parsed,
        viewID: viewID ?? '',
      } as Partial<SemanticLegendSettings>;
    } catch (error) {
      // If parsing fails, remove the corrupted data and return undefined
      console.warn('Corrupted semantic legend data in localStorage:', error);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
      return DEFAULT_SEMANTIC_LEGEND_SETTINGS;
    }
  }, [viewName, viewID]);

  const combinedError = useMemo(
    () =>
      semanticLegendSettingsError ||
      metricStatisticsError ||
      metricStatisticsForRunError ||
      configValuesError ||
      configValuesErrorCategories,
    [
      semanticLegendSettingsError,
      metricStatisticsError,
      metricStatisticsForRunError,
      configValuesError,
      configValuesErrorCategories,
    ]
  );

  // If we have a saved spec and we have a real viewID and not loading, create it and clear storage
  useEffect(() => {
    if (
      savedSemanticLegendFromStorage &&
      viewID &&
      !semanticLegendSettingsLoading
    ) {
      createSemanticLegendSettings(savedSemanticLegendFromStorage);
      safeLocalStorage.removeItem(`semanticLegendSettings-${viewName}`);
    }
  }, [
    savedSemanticLegendFromStorage,
    viewID,
    semanticLegendSettingsLoading,
    createSemanticLegendSettings,
    viewName,
  ]);

  const metricStatistics = useMemo(() => {
    return {
      ...metricStatisticsAggregation,
      [YAxisValues.Config_Number]: configValuesNumberAggregation,
      [YAxisValues.Config_Categories]: {
        min: 0,
        max: 0,
      },
    };
  }, [metricStatisticsAggregation, configValuesNumberAggregation]);

  // Ref for metric statistics comparison (most frequently changing data)
  const prevMetricStatsRef = useRef<MetricStatisticsAggregation | null>(null);
  const prevConfigValuesCategoriesRef = useRef<string[] | null>(null);

  // Ref to prevent infinite loops from automatic updates
  const isAutoUpdatingRef = useRef(false);

  // Effect for bucket recalculation - uses ref for metric stats
  useEffect(() => {
    if (!viewID || combinedLoading) {
      return;
    }

    // Check if the metric statistics aggregation has changed if we are not skipping metric statistics or config values aggregation
    const metricStatisticsAggregationHasChanged =
      (!shouldSkipMetricStatistics || !shouldSkipConfigValuesAggregation) &&
      prevMetricStatsRef.current !== null &&
      Object.keys(prevMetricStatsRef.current).length > 0 &&
      !isEqual(prevMetricStatsRef.current, metricStatistics);

    // Check if the config values categories have changed if we are not skipping config values categories
    const configValuesCategoriesChanged =
      !shouldSkipConfigValuesCategories &&
      prevConfigValuesCategoriesRef.current !== null &&
      Object.keys(prevConfigValuesCategoriesRef.current).length > 0 &&
      !isEqual(prevConfigValuesCategoriesRef.current, configValuesCategories);

    // Only update if this is a meaningful change, not just initial loading
    if (
      !combinedLoading &&
      constructedSemanticLegendData &&
      (metricStatisticsAggregationHasChanged || configValuesCategoriesChanged)
    ) {
      // Set flag to prevent loops
      isAutoUpdatingRef.current = true;

      updateSemanticLegendSettings({
        ...constructedSemanticLegendData,
        metricStatistics,
        configValuesCategories,
        shouldRecalculateBuckets:
          metricStatisticsAggregationHasChanged ||
          configValuesCategoriesChanged,
      });

      // Reset flag after a delay to allow the mutation cycle to complete
      setTimeout(() => {
        isAutoUpdatingRef.current = false;
      }, 50);
    }

    prevMetricStatsRef.current = metricStatistics;
    prevConfigValuesCategoriesRef.current = configValuesCategories;
  }, [
    metricStatistics,
    combinedLoading,
    constructedSemanticLegendData,
    configValuesCategories,
    viewID,
    updateSemanticLegendSettings,
    shouldSkipMetricStatistics,
    shouldSkipConfigValuesCategories,
    shouldSkipConfigValuesAggregation,
  ]);

  // Retrieve the semantic legend settings
  const {data: semanticLegendSettingsData, specPreviouslyCreated} =
    useMemo(() => {
      const base = {
        error: combinedError,
        loading: combinedLoading,
      };

      // If a valid semantic legend settings object exists, use it
      // we can use `currentMetric` to check this since we only have it when it destructures properly off a well-formed response
      // Also ensure all data is loaded to prevent race conditions
      if (currentKey && !combinedLoading) {
        return {
          ...base,
          data: {
            ...currentSemanticLegendSettings,
            metricStatistics,
            configValuesCategories,
            configValuesForRun,
            metricStatisticsForRun,
            runColors: currentRunColors,
          },
          specPreviouslyCreated: true,
        };
      }
      // if no semantic legend settings object exists, check local storage for a saved semantic legend settings object
      // Only use saved settings if dependent data is loaded to prevent race conditions
      if (savedSemanticLegendFromStorage && !combinedLoading) {
        // For saved settings, we need to calculate runs and colors based on the saved yAxisValue
        const savedRuns = getRuns(
          savedSemanticLegendFromStorage.yAxisValue as YAxisValues,
          configValuesForRunCategories,
          configValuesForRun,
          metricStatisticsForRun
        );
        const savedRunColors = calculateRunColorsOptimized(
          savedRuns,
          savedSemanticLegendFromStorage as SemanticLegendSettings,
          semanticLegendPalette
        );
        return {
          ...base,
          data: {
            ...(savedSemanticLegendFromStorage as SemanticLegendSettings),
            runColors: savedRunColors,
          },
          specPreviouslyCreated: true,
        };
      }

      // default to the default semantic legend settings
      return {
        ...base,
        data: {
          ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
          viewID: viewID ?? '',
        },
        specPreviouslyCreated: false,
      };
    }, [
      combinedError,
      combinedLoading,
      currentKey,
      currentSemanticLegendSettings,
      currentRunColors,
      semanticLegendPalette,
      savedSemanticLegendFromStorage,
      configValuesForRunCategories,
      configValuesForRun,
      metricStatisticsForRun,
      viewID,
      metricStatistics,
      configValuesCategories,
    ]);

  return useMemo(
    () => ({
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      combinedLoading,
    }),
    [
      semanticLegendSettingsData,
      semanticLegendSettingsLoading,
      semanticLegendSettingsError,
      specPreviouslyCreated,
      combinedLoading,
    ]
  );
};
